import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Boxes, Trash2, Pencil, Inbox, AlertCircle, Crown, Infinity as InfinityIcon, QrCode } from "lucide-react";
import { toast } from "sonner";
import { containersApi, formatError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { PremiumModal } from "./PremiumLockTile";
import { BoxColorSwatch } from "./BoxColorSwatch";

const STATUS_PILL = {
  active: { bg: "bg-[#DCEDF0]", border: "border-[#277A82]", chip: "bg-[#1F5566] text-white", label: "Active" },
  empty:  { bg: "bg-[#E8F0EA]", border: "border-[#4A8A78]", chip: "bg-[#4A8A78] text-white", label: "Unused" },
};

const ConfirmBar = ({ message, onConfirm, onCancel, busy }) => (
  <motion.div
    initial={{ y: 12, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    className="tup-card p-3 border-2 border-[#D95D39] bg-[#FBE2DA] flex items-center gap-2"
    data-testid="boxes-confirm-bar"
  >
    <AlertCircle className="w-4 h-4 text-[#8C2A0F] shrink-0" />
    <div className="flex-1 text-xs text-[#8C2A0F] font-semibold">{message}</div>
    <button
      type="button"
      onClick={onCancel}
      disabled={busy}
      data-testid="boxes-confirm-cancel"
      className="text-xs font-semibold text-[#4F5D75] px-2 py-1 rounded hover:bg-white/60 disabled:opacity-50"
    >
      Cancel
    </button>
    <button
      type="button"
      onClick={onConfirm}
      disabled={busy}
      data-testid="boxes-confirm-yes"
      className="text-xs font-bold text-white bg-[#D95D39] hover:bg-[#B04A2A] px-2.5 py-1.5 rounded disabled:opacity-50"
    >
      {busy ? "Deleting…" : "Yes, delete"}
    </button>
  </motion.div>
);

export const BoxesOverlay = ({ open, onClose, onChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // box object
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setBoxes(await containersApi.boxes());
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPendingDelete(null);
      setConfirmAll(false);
      load();
    }
  }, [open]);

  const doDeleteBox = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await containersApi.deleteBox(pendingDelete.box_number);
      setBoxes((prev) => prev.filter((b) => b.box_number !== pendingDelete.box_number));
      toast.success(`Box ${pendingDelete.box_number} deleted`);
      setPendingDelete(null);
      onChange?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const setBoxColor = async (boxNumber, color) => {
    // Optimistic update — the API call is fire-and-forget; we just toast
    // on failure and revert to whatever the server says next time the
    // overlay reopens. Keeps the picker feeling instant.
    const prev = boxes;
    setBoxes((bs) => bs.map((b) => (b.box_number === boxNumber ? { ...b, color } : b)));
    try {
      await containersApi.setBoxColor(boxNumber, color);
    } catch (err) {
      toast.error(formatError(err));
      setBoxes(prev);
    }
  };

  const doDeleteAll = async () => {
    setBusy(true);
    try {
      await containersApi.deleteAllBoxes();
      setBoxes([]);
      toast.success("All boxes deleted");
      setConfirmAll(false);
      onChange?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          data-testid="boxes-overlay-backdrop"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4 max-h-[88vh] overflow-y-auto flex flex-col"
            data-testid="boxes-overlay"
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl brand-gradient-bg flex items-center justify-center">
                  <Boxes className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                    Your boxes
                  </div>
                  <div className="font-heading text-lg font-bold text-[#2D3142]">
                    {boxes.length} {boxes.length === 1 ? "box" : "boxes"}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="boxes-overlay-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#4F5D75] leading-relaxed mb-4">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1F5566] mr-1.5 align-middle" />
              Blue = active box (food inside)
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#4A8A78] ml-3 mr-1.5 align-middle" />
              Green = unused box (empty)
            </p>

            <div className="flex-1 overflow-y-auto -mx-1 px-1">
              {loading ? (
                <div className="tup-card p-6 text-center text-sm text-[#8A93A6]">Loading…</div>
              ) : boxes.length === 0 && user?.is_premium ? (
                <div className="tup-card p-6 text-center" data-testid="boxes-empty">
                  <Inbox className="w-7 h-7 mx-auto text-[#8A93A6]" />
                  <div className="font-heading text-base font-semibold text-[#2D3142] mt-3">
                    No boxes yet
                  </div>
                  <p className="text-xs text-[#4F5D75] mt-2 leading-relaxed">
                    Log your first container to claim a box.
                  </p>
                </div>
              ) : (
                <ul className="grid grid-cols-2 gap-3" data-testid="boxes-grid">
                  {boxes.map((b) => {
                    const style = STATUS_PILL[b.status] || STATUS_PILL.active;
                    const d = b.days_until_expiry;
                    const isActive = b.status === "active";
                    const isPastDue = isActive && typeof d === "number" && d < 0;
                    const isSoon = isActive && typeof d === "number" && d >= 0 && d <= 2;
                    const pastDueDays = isPastDue ? Math.abs(d) : 0;
                    return (
                      <li
                        key={b.qr_code}
                        className={`relative rounded-2xl border-2 p-3 ${style.bg} ${style.border}`}
                        data-testid={`box-tile-${b.box_number}`}
                      >
                        <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Pre-fill the Add Container page so the user
                              // can re-use this specific physical sticker.
                              onClose?.();
                              navigate(`/add?qr=${encodeURIComponent(b.qr_code)}&box=${b.box_number}`);
                            }}
                            disabled={busy}
                            data-testid={`box-edit-${b.box_number}`}
                            aria-label={`Log a new container in Box ${b.box_number}`}
                            className="w-7 h-7 rounded-full bg-white/85 hover:bg-white text-[#1F5566] flex items-center justify-center shadow-sm disabled:opacity-50"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDelete(b);
                              setConfirmAll(false);
                            }}
                            disabled={busy}
                            data-testid={`box-delete-${b.box_number}`}
                            aria-label={`Delete Box ${b.box_number}`}
                            className="w-7 h-7 rounded-full bg-white/85 hover:bg-white text-[#9C3E1D] flex items-center justify-center shadow-sm disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (b.container_id) {
                              onClose?.();
                              navigate(`/container/${b.container_id}`);
                            }
                          }}
                          disabled={!b.container_id}
                          className="text-left w-full pr-14 pb-1"
                        >
                          <div className="flex items-center gap-1.5">
                            <div
                              className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${style.chip}`}
                            >
                              {style.label}
                            </div>
                            {isPastDue && (
                              <span
                                aria-label="Past best-by"
                                data-testid={`box-caution-red-${b.box_number}`}
                                className="inline-block w-2 h-2 rounded-full bg-[#D95D39] opacity-80"
                              />
                            )}
                            {isSoon && (
                              <span
                                aria-label="Soon to spoil"
                                data-testid={`box-caution-orange-${b.box_number}`}
                                className="inline-block w-2 h-2 rounded-full bg-[#E07A5F] opacity-80"
                              />
                            )}
                          </div>
                          <div className="font-heading text-lg font-extrabold text-[#2D3142] mt-2 leading-none flex items-center gap-1.5">
                            <span>Box {b.box_number}</span>
                            {b.logged_via_scan && (
                              <QrCode
                                className="w-4 h-4 text-[#4A8A78]"
                                strokeWidth={2.4}
                                data-testid={`scanned-badge-box-${b.box_number}`}
                                aria-label="Logged via QR scan"
                              />
                            )}
                          </div>
                          <div
                            className="text-xs text-[#2D3142] mt-1.5 line-clamp-2 leading-snug min-h-[2rem]"
                            data-testid={`box-name-${b.box_number}`}
                          >
                            {b.status === "empty"
                              ? ""
                              : (b.name && b.name !== "—" ? b.name : "Untitled")}
                          </div>
                          {isPastDue && (
                            <div
                              className="text-[10px] font-bold text-[#9C3E1D] mt-1"
                              data-testid={`box-pastdue-${b.box_number}`}
                            >
                              Past due {pastDueDays}d
                            </div>
                          )}
                          <div className="text-[10px] text-[#4F5D75] font-mono mt-2 truncate">
                            {b.qr_code}
                          </div>
                        </button>
                        {/* Color swatch — purely visual organiser. Sits in
                            the bottom-right corner so it doesn't compete
                            with the trash/edit icons in the top corner. */}
                        <div className="absolute bottom-1.5 right-1.5">
                          <BoxColorSwatch
                            value={b.color || "clear"}
                            onChange={(c) => setBoxColor(b.box_number, c)}
                            testid={`box-color-${b.box_number}`}
                          />
                        </div>
                      </li>
                    );
                  })}
                  {!user?.is_premium && (
                    <li
                      key="unlimited-slots-upsell"
                      data-testid="box-tile-unlimited"
                      className="relative rounded-2xl border-2 border-dashed border-[#277A82] bg-gradient-to-br from-[#E8F4F6] to-[#DCEDF0] p-3"
                    >
                      <button
                        type="button"
                        onClick={() => setPremiumOpen(true)}
                        data-testid="boxes-unlock-unlimited-btn"
                        className="text-left w-full"
                      >
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1F5566] text-white">
                          <Crown className="w-2.5 h-2.5" /> Premium
                        </div>
                        <div className="flex items-center gap-1.5 font-heading text-lg font-extrabold text-[#1F5566] mt-2 leading-none">
                          <InfinityIcon className="w-5 h-5" />
                          <span>Unlimited</span>
                        </div>
                        <div className="text-xs text-[#2D3142] mt-1.5 line-clamp-2 leading-snug min-h-[2rem]">
                          Free plan caps at 3 active boxes. Tap to unlock more.
                        </div>
                        <div className="text-[10px] text-[#277A82] font-semibold mt-2 truncate">
                          Upgrade →
                        </div>
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Confirmation bars */}
            <div className="mt-4 space-y-2">
              {pendingDelete && (
                <ConfirmBar
                  busy={busy}
                  message={`Delete Box ${pendingDelete.box_number} and its history?`}
                  onConfirm={doDeleteBox}
                  onCancel={() => setPendingDelete(null)}
                />
              )}
              {confirmAll && (
                <ConfirmBar
                  busy={busy}
                  message={`Delete ALL ${boxes.length} boxes? This can't be undone.`}
                  onConfirm={doDeleteAll}
                  onCancel={() => setConfirmAll(false)}
                />
              )}
            </div>

            {/* Bottom action: Delete all */}
            {boxes.length > 0 && !confirmAll && (
              <button
                type="button"
                onClick={() => { setConfirmAll(true); setPendingDelete(null); }}
                disabled={busy}
                data-testid="boxes-delete-all-btn"
                className="mt-4 self-end inline-flex items-center gap-1.5 text-xs font-semibold text-[#9C3E1D] bg-white border-2 border-[#D95D39] hover:bg-[#FBEAE5] rounded-xl px-3 py-2 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete all boxes
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />
    </AnimatePresence>
  );
};
